import { useEffect, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { ServicesUrlEndpoints } from "src/services/servicesEndpointConstant";
import { api } from "src/util/api.service";
import { saveToLocal, removeFromLocal } from "src/util/cache.service";
import { LOCAL_KEYS } from "src/util/util.constant";
import Notification from "src/util/libs/notification";
import { useLoginContext } from "src/contextApi/LoginContext";
import type { LoginFormValues } from "src/types/common.types";
import { ROUTE_CONSTANTS } from "src/routes/routeConstant";

interface LoginResponse {
  response?: {
    token?: {
      access_token: string;
      refresh_token: string;
    };
    role?: string;
    details?: unknown;
  };
}

interface ErrorResponse {
  response?: {
    data?: {
      status?: number;
      errorCode?: number;
      message?: string;
    };
  };
}

interface UseLoginContainerReturn {
  showClientId: boolean;
  getUserPending: boolean;
  getUserNameData: unknown;
  userName: string;
  startTimer: boolean;
  otp: string;
  setOtp: (value: string) => void;
  otpRequestLoader: boolean;
  setStartTimer: (value: boolean) => void;
  isShowOtp: boolean;
  setIsShowOtp: (value: boolean) => void;
  setUserName: (value: string) => void;
  handleSubmit: (values: LoginFormValues) => void;
  loginRequestLoader: boolean;
  loginData: LoginResponse | null;
  otpRequest: (args: { username: string }) => Promise<{ data?: { response?: { sessionId?: string } } }>;
  isShowContinueBtn: boolean;
  setIsShowContinueBtn: (value: boolean) => void;
  setShowClientId: (value: boolean) => void;
  disabledLoggedBtn: boolean;
  sessionId: string | null;
}

export const useLoginContainer = (): UseLoginContainerReturn => {
  const navigate = useNavigate();
  const { setToken } = useLoginContext();

  const [isShowContinueBtn, setIsShowContinueBtn] = useState(true);
  const [showClientId, setShowClientId] = useState(false);
  const [disabledLoggedBtn, setDisabledLoggedBtn] = useState(false);

  const [userName, setUserName] = useState("");
  const [otp, setOtp] = useState("");
  const [isShowOtp, setIsShowOtp] = useState(false);
  const [sendOtpPayload, setSendOtpPayload] = useState<LoginFormValues | null>(null);
  const [startTimer, setStartTimer] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);

  const [getUserPending] = useState(false);
  const [getUserNameData] = useState<unknown>(null);

  const rememberMeRef = useRef(false);

  const loginRequest = useMutation<LoginResponse, ErrorResponse, LoginFormValues & { queryParam?: Record<string, unknown> }>({
    mutationFn: async (payload) => {
      const { queryParam, userName: payloadUserName, password, ...rest } = payload;

      const body = { username: payloadUserName, password, ...rest };

      const response = await api.post<LoginResponse>(
        ServicesUrlEndpoints.AUTH.LOGIN,
        body,
        {
          params: queryParam,
        }
      );

      return response.data;
    },
    onError: (error) => {
      const msg = error.response?.data?.message;
      if (msg) {
        Notification.error({ message: msg });
      } else {
        Notification.error({
          message: "Login failed. Please try again.",
        });
      }
    },
  });

  const { data: loginData = null, isPending: loginRequestLoader } = loginRequest;

  const otpRequestMutation = useMutation<
    { data?: { response?: { sessionId?: string } } },
    ErrorResponse,
    { username: string }
  >({
    mutationFn: async ({ username }) => {
      return api.post<{ response?: { sessionId?: string } }>(
        ServicesUrlEndpoints.AUTH.SEND_OTP,
        {},
        {
          headers: { "Content-Type": "application/json" },
          params: { username },
        }
      );
    },
    onSuccess: (response) => {
      const sessionIdFromApi: string | undefined = response?.data?.response?.sessionId;
      if (sessionIdFromApi) {
        setSessionId(sessionIdFromApi);
      }
      setStartTimer(true);
    },
    onError: (error: ErrorResponse) => {
      setStartTimer(false);
      const msg = error.response?.data?.message;
      Notification.error({
        message:
          msg ??
          "Failed to send OTP. Please click the 'Send New Code' button or try again later.",
      });
    },
  });

  const otpRequest = otpRequestMutation.mutateAsync;
  const otpRequestLoader = otpRequestMutation.isPending;

  useEffect(() => {
    if (!loginData?.response?.token?.access_token) return;

    const accessToken = loginData.response.token.access_token;
    const refreshToken = loginData.response.token.refresh_token;
    const role = loginData.response.role;
    const details = loginData.response.details;

    saveToLocal(LOCAL_KEYS.REFRESH_TOKEN, refreshToken, false, true);
    saveToLocal(LOCAL_KEYS.TOKEN, accessToken, false, true);
    saveToLocal(LOCAL_KEYS.USER_DETAIL, details, true, true);
    saveToLocal(LOCAL_KEYS.ROLE, role, false, true);
    saveToLocal(LOCAL_KEYS.IS_AUTHENTICATED, true, false, true);
    saveToLocal(LOCAL_KEYS.SHOW_DISTRIBUTOR_POPOVER, true, false, false);
    saveToLocal(LOCAL_KEYS.REMEMBER_ME, rememberMeRef.current, true, true);
    setToken(accessToken);

    navigate(ROUTE_CONSTANTS.DASHBOARD, { replace: true });
  }, [loginData, navigate, setToken]);

  const loginLogic = (payload: LoginFormValues, realmNameParam: string | null = null) => {
    const newPayload: LoginFormValues = {
      userName: payload.userName,
      password: payload.password,
      remember: payload.remember,
      clientId: payload.clientId ?? realmNameParam ?? undefined,
    };

    rememberMeRef.current = Boolean(payload.remember);

    loginRequest.mutate(newPayload, {
      onSuccess: () => {
        // success handled in useEffect
      },
      onError: async (error, variables) => {
        const data = variables as LoginFormValues;
        setSendOtpPayload(data);

        const errorCode = error.response?.data?.errorCode;
        const status = error.response?.data?.status;

        if (status === 400 && errorCode === 0) {
          setIsShowOtp(true);

          const response = await otpRequest({ username: newPayload.userName });
          const sessionIdFromApi: string | undefined =
            response?.data?.response?.sessionId;
          if (sessionIdFromApi) {
            setSessionId(sessionIdFromApi);
          }
          setStartTimer(true);
        }

        setDisabledLoggedBtn(false);
        removeFromLocal(LOCAL_KEYS.USER_DETAIL);
      },
    });
  };

  const handleSubmit = (payload: LoginFormValues) => {
    if (!isShowOtp) {
      loginLogic(payload);
    } else {
      if (!sendOtpPayload) return;
      loginRequest.mutate({
        ...sendOtpPayload,
        queryParam: {
          otp,
          sessionId,
        },
      });
    }
  };

  return {
    showClientId,
    getUserPending,
    getUserNameData,
    userName,
    startTimer,
    otp,
    setOtp,
    otpRequestLoader,
    setStartTimer,
    isShowOtp,
    setIsShowOtp,
    setUserName,
    handleSubmit,
    loginRequestLoader,
    loginData,
    otpRequest,
    isShowContinueBtn,
    setIsShowContinueBtn,
    setShowClientId,
    disabledLoggedBtn,
    sessionId,
  };
};
